import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Laptop, School, FileCheck, MapPin, Palette, X, ArrowUpDown, SlidersHorizontal, ChevronDown } from 'lucide-react';
import type { PeRegister, ProLeervormFilter as ProLeervorm, ProSortering, ProZoekMeta, Vakgebied } from '../../services/proAcademyApi';
import type { ProHoofdmapInfo } from '../../services/professionalAcademyApi';
import { AFSTAND_STAPPEN, bewaarEigenPostcode, normaliseerPostcode4 } from '../../utils/pc4Afstand';
import { VakgebiedZoeker } from './VakgebiedZoeker';
import { LEGE_FILTERS, heeftFilters, type ProFilters } from './proAcademyFilters';

const DAGEN = [1, 2, 3, 4, 5, 6];
const REGISTERS_FALLBACK: ProZoekMeta['registers'] = [
  { key: 'NVAB', label: 'NVAB', doelgroep: 'Bedrijfsartsen' },
  { key: 'RSC_RNVC', label: 'RSC / Rnvc', doelgroep: 'Casemanagers' },
  { key: 'NOBCO_EIA', label: 'NOBCO / EIA', doelgroep: 'Coaches' },
  { key: 'NIP', label: 'NIP', doelgroep: 'Psychologen' },
];

/** Actieve filter-tags (labels + wis-actie) — gedeeld door het zijpaneel en de badges in de workspace. */
function filterTags(
  filters: ProFilters,
  vakgebied: Omit<Vakgebied, 'score'> | null,
  hoofdmappen: ProHoofdmapInfo[],
  registers: ProZoekMeta['registers'],
  t: (key: string, opts?: Record<string, unknown>) => string,
  zet: (patch: Partial<ProFilters>) => void
): Array<{ key: string; label: string; wis: () => void }> {
  const dagLabels = t('proAcademy.dagen', { defaultValue: 'Ma,Di,Wo,Do,Vr,Za' }).split(',');
  const tags: Array<{ key: string; label: string; wis: () => void }> = [];
  if (vakgebied) tags.push({ key: 'vak', label: vakgebied.label, wis: () => zet({ vak: null }) });
  if (filters.map) {
    const m = hoofdmappen.find((h) => h.key === filters.map);
    tags.push({ key: 'map', label: m ? `${m.emoji} ${m.label}` : filters.map, wis: () => zet({ map: null }) });
  }
  if (filters.q) tags.push({ key: 'q', label: `“${filters.q}”`, wis: () => zet({ q: null }) });
  if (filters.alles) tags.push({ key: 'alles', label: t('proAcademy.alleCursussen', { defaultValue: 'Alle cursussen' }), wis: () => zet({ alles: false }) });
  for (const r of filters.reg) tags.push({ key: `reg-${r}`, label: registers.find((x) => x.key === r)?.label ?? r, wis: () => zet({ reg: filters.reg.filter((x) => x !== r) }) });
  if (filters.pe > 0) tags.push({ key: 'pe', label: t('proAcademy.tagPe', { defaultValue: 'min. {{n}} PE-punten', n: filters.pe }), wis: () => zet({ pe: 0 }) });
  if (filters.vorm) tags.push({ key: 'vorm', label: filters.vorm === 'e_learning' ? t('proAcademy.eLearning', { defaultValue: 'E-Learning' }) : t('proAcademy.klassikaal', { defaultValue: 'Klassikaal / Workshop' }), wis: () => zet({ vorm: null, km: null }) });
  if (filters.examen) tags.push({ key: 'examen', label: t('proAcademy.inclExamen', { defaultValue: 'Inclusief examen' }), wis: () => zet({ examen: false }) });
  for (const d of filters.dag) tags.push({ key: `dag-${d}`, label: dagLabels[d - 1] ?? String(d), wis: () => zet({ dag: filters.dag.filter((x) => x !== d) }) });
  if (filters.vorm === 'klassikaal' && filters.km) tags.push({ key: 'km', label: t('proAcademy.tagKm', { defaultValue: '≤ {{n}} km', n: filters.km }), wis: () => zet({ km: null }) });
  return tags;
}

const Groep: React.FC<{ titel: string; children: React.ReactNode; className?: string }> = ({ titel, children, className = '' }) => (
  <fieldset className={`space-y-2 ${className}`}>
    <legend className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{titel}</legend>
    {children}
  </fieldset>
);

/**
 * Filter-, sorteer- & zoekpaneel in de linkerzijbalk (spec §3): A vakgebied-zoeker, sortering,
 * hoofdmappen (children), B registers + PE-slider, C leervorm, D weekdag-strip, E hobby-schakelaar,
 * F reistijd-slider (postcode lokaal). Onder lg klapt het paneel uit via een "Filters"-knop.
 */
export const ProAcademyFilterbalk: React.FC<{
  filters: ProFilters;
  vakgebied: Omit<Vakgebied, 'score'> | null;
  meta: ProZoekMeta | undefined;
  hoofdmappen: ProHoofdmapInfo[];
  eigenPostcode: string | null;
  onEigenPostcode: (pc4: string | null) => void;
  onChange: (next: ProFilters) => void;
  /** Extra filtergroep (bijv. de hoofdmap-chips) direct onder de sortering. */
  children?: React.ReactNode;
}> = ({ filters, vakgebied, meta, hoofdmappen, eigenPostcode, onEigenPostcode, onChange, children }) => {
  const { t } = useTranslation('pages');
  const [postcodeInvoer, setPostcodeInvoer] = useState('');
  const [mobielOpen, setMobielOpen] = useState(false);
  const registers = meta?.registers ?? REGISTERS_FALLBACK;
  const peMax = meta?.pe_max ?? 20;
  const zet = (patch: Partial<ProFilters>) => onChange({ ...filters, ...patch });
  const dagLabels = t('proAcademy.dagen', { defaultValue: 'Ma,Di,Wo,Do,Vr,Za' }).split(',');
  const klassikaal = filters.vorm === 'klassikaal';
  const kmIndex = Math.max(0, AFSTAND_STAPPEN.findIndex((s) => s === filters.km));
  const aantalActief = filterTags(filters, vakgebied, hoofdmappen, registers, t, zet).length + (filters.hobby ? 1 : 0);

  const bevestigPostcode = () => {
    const pc = normaliseerPostcode4(postcodeInvoer);
    if (!pc) return;
    bewaarEigenPostcode(pc);
    onEigenPostcode(pc);
    setPostcodeInvoer('');
  };

  const knop = (actief: boolean, extra = '') =>
    `inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${actief ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'} ${extra}`;

  return (
    <div role="search" aria-label={t('proAcademy.filterbalk', { defaultValue: 'Filter- en zoekpaneel' })}>
      <button
        type="button"
        onClick={() => setMobielOpen((o) => !o)}
        aria-expanded={mobielOpen}
        aria-controls="pro-academy-filters"
        className="flex w-full items-center justify-between gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-900 lg:hidden"
      >
        <span className="inline-flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-gray-500" aria-hidden="true" />
          {aantalActief > 0
            ? t('proAcademy.filtersKnopActief', { defaultValue: 'Filters ({{n}} actief)', n: aantalActief })
            : t('proAcademy.filtersKnop', { defaultValue: 'Filters' })}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition ${mobielOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      <div id="pro-academy-filters" className={`${mobielOpen ? 'mt-3 block' : 'hidden'} space-y-5 rounded-2xl border border-gray-200 bg-white p-4 lg:mt-0 lg:block`}>
        {/* A. Vakgebied- & specialismenzoeker */}
        <Groep titel={t('proAcademy.zoekKop', { defaultValue: 'Zoeken' })}>
          <VakgebiedZoeker
            vak={vakgebied}
            vrijeTekst={filters.q}
            onKiesVak={(v) => zet({ vak: v.professional_type, q: null, map: null })}
            onVrijeTekst={(q) => zet({ q })}
            onWis={() => zet({ vak: null, q: null })}
          />
        </Groep>

        <Groep titel={t('proAcademy.sorteer', { defaultValue: 'Sorteren' })}>
          <label className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900">
            <ArrowUpDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
            <span className="sr-only">{t('proAcademy.sorteer', { defaultValue: 'Sorteren' })}</span>
            <select value={filters.sort} onChange={(e) => zet({ sort: e.target.value as ProSortering })} className="w-full bg-transparent text-sm text-gray-900 outline-none">
              <option value="relevantie">{t('proAcademy.sortRelevantie', { defaultValue: 'Relevantie' })}</option>
              <option value="start">{t('proAcademy.sortStart', { defaultValue: 'Eerstvolgende start' })}</option>
              <option value="prijs">{t('proAcademy.sortPrijs', { defaultValue: 'Prijs (laag → hoog)' })}</option>
              <option value="pe">{t('proAcademy.sortPe', { defaultValue: 'Meeste PE-punten' })}</option>
            </select>
          </label>
        </Groep>

        {children}

        {/* B. Register & accreditatie */}
        <Groep titel={t('proAcademy.registers', { defaultValue: 'Register & accreditatie' })}>
          <div className="flex flex-wrap gap-1.5">
            {registers.map((r) => {
              const actief = filters.reg.includes(r.key);
              return (
                <button
                  key={r.key}
                  type="button"
                  aria-pressed={actief}
                  title={r.doelgroep}
                  onClick={() => zet({ reg: actief ? filters.reg.filter((x) => x !== r.key) : [...filters.reg, r.key as PeRegister] })}
                  className={knop(actief)}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
          <label className="block text-xs text-gray-700">
            <span className="flex justify-between gap-2">
              {t('proAcademy.minPe', { defaultValue: 'Min. PE-punten:' })} <strong className="tabular-nums text-gray-900">{filters.pe}</strong>
            </span>
            <input type="range" min={0} max={peMax} step={1} value={filters.pe} onChange={(e) => zet({ pe: parseInt(e.target.value, 10) })} className="mt-1 w-full accent-[#0B1F3A]" aria-label={t('proAcademy.minPeAria', { defaultValue: 'Minimaal aantal PE-punten' })} />
          </label>
        </Groep>

        {/* C. Leervorm & intensiteit */}
        <Groep titel={t('proAcademy.leervorm', { defaultValue: 'Leervorm & intensiteit' })}>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" aria-pressed={filters.vorm === 'e_learning'} onClick={() => zet({ vorm: filters.vorm === 'e_learning' ? null : ('e_learning' as ProLeervorm), km: null, dag: filters.vorm === 'e_learning' ? filters.dag : [] })} className={knop(filters.vorm === 'e_learning')}>
              <Laptop className="h-3.5 w-3.5" aria-hidden="true" />{t('proAcademy.eLearning', { defaultValue: 'E-Learning' })}
            </button>
            <button type="button" aria-pressed={klassikaal} onClick={() => zet({ vorm: klassikaal ? null : ('klassikaal' as ProLeervorm), km: klassikaal ? null : filters.km })} className={knop(klassikaal)}>
              <School className="h-3.5 w-3.5" aria-hidden="true" />{t('proAcademy.klassikaal', { defaultValue: 'Klassikaal / Workshop' })}
            </button>
            <button type="button" aria-pressed={filters.examen} onClick={() => zet({ examen: !filters.examen })} className={knop(filters.examen)}>
              <FileCheck className="h-3.5 w-3.5" aria-hidden="true" />{t('proAcademy.inclExamen', { defaultValue: 'Inclusief examen' })}
            </button>
          </div>
        </Groep>

        {/* D. Agenda & beschikbaarheid */}
        <Groep titel={t('proAcademy.agenda', { defaultValue: 'Agenda & beschikbaarheid' })}>
          <div className="grid grid-cols-6 gap-1">
            {DAGEN.map((d) => {
              const actief = filters.dag.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={actief}
                  disabled={filters.vorm === 'e_learning'}
                  onClick={() => zet({ dag: actief ? filters.dag.filter((x) => x !== d) : [...filters.dag, d].sort() })}
                  className={`h-8 rounded-md border text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-30 ${actief ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'}`}
                >
                  {dagLabels[d - 1]}
                </button>
              );
            })}
          </div>
        </Groep>

        {/* F. Reistijd (alleen klassikaal; postcode blijft lokaal) */}
        <Groep titel={t('proAcademy.reistijdKop', { defaultValue: 'Reisafstand' })}>
          <div className={`text-xs ${klassikaal ? 'text-gray-700' : 'text-gray-400'}`} title={klassikaal ? undefined : t('proAcademy.reistijdUit', { defaultValue: 'Alleen bij Klassikaal / Workshop' })}>
            {klassikaal && !eigenPostcode ? (
              <form onSubmit={(e) => { e.preventDefault(); bevestigPostcode(); }} className="space-y-1.5">
                <label className="flex items-center gap-1.5" htmlFor="pro-postcode">
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('proAcademy.postcodeVraag', { defaultValue: 'Uw postcode (4 cijfers):' })}
                </label>
                <div className="flex items-center gap-1.5">
                  <input id="pro-postcode" inputMode="numeric" maxLength={4} value={postcodeInvoer} onChange={(e) => setPostcodeInvoer(e.target.value.replace(/\D/g, ''))} placeholder="1234" className="w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 placeholder:text-gray-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/30" />
                  <button type="submit" className="rounded-md bg-[#0B1F3A] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#1A3459] disabled:opacity-40" disabled={!normaliseerPostcode4(postcodeInvoer)}>OK</button>
                </div>
                <p className="text-[11px] text-gray-500">🔒 {t('proAcademy.postcodeLokaal', { defaultValue: 'blijft in uw browser' })}</p>
              </form>
            ) : (
              <label className="block">
                <span className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('proAcademy.maxAfstand', { defaultValue: 'Max. afstand:' })}
                  </span>
                  <strong className="tabular-nums">{filters.km ? `${filters.km} km` : t('proAcademy.heelNederland', { defaultValue: 'Heel Nederland' })}</strong>
                </span>
                <input
                  type="range"
                  min={0}
                  max={AFSTAND_STAPPEN.length - 1}
                  step={1}
                  disabled={!klassikaal}
                  value={filters.km ? kmIndex : AFSTAND_STAPPEN.length - 1}
                  onChange={(e) => zet({ km: AFSTAND_STAPPEN[parseInt(e.target.value, 10)] })}
                  className="mt-1 w-full accent-[#0B1F3A] disabled:cursor-not-allowed"
                  aria-label={t('proAcademy.maxAfstandAria', { defaultValue: 'Maximale reisafstand in kilometers' })}
                />
                {klassikaal && eigenPostcode && (
                  <button type="button" onClick={() => { bewaarEigenPostcode(null); onEigenPostcode(null); zet({ km: null }); }} className="text-[11px] text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline" title={t('proAcademy.postcodeLokaal', { defaultValue: 'blijft in uw browser' })}>
                    {t('proAcademy.postcodeWijzig', { defaultValue: 'vanaf {{pc}} (wijzig)', pc: eigenPostcode })}
                  </button>
                )}
              </label>
            )}
          </div>
        </Groep>

        {/* E. Hobby-schakelaar */}
        <button
          type="button"
          role="switch"
          aria-checked={filters.hobby}
          onClick={() => zet({ hobby: !filters.hobby, map: filters.hobby && filters.map === 'passie' ? null : filters.map })}
          className={knop(filters.hobby, `w-full justify-center ${filters.hobby ? '!border-[#FF9800] !bg-[#FF9800] !text-gray-900' : ''}`)}
        >
          <Palette className="h-3.5 w-3.5" aria-hidden="true" />
          {t('proAcademy.toonHobby', { defaultValue: 'Toon ook hobbymatige opleidingen' })}
        </button>
      </div>
    </div>
  );
};

/** Actieve filterbadges + "alles wissen" bovenaan de hoofdworkspace. */
export const ProAcademyActieveFilters: React.FC<{
  filters: ProFilters;
  vakgebied: Omit<Vakgebied, 'score'> | null;
  meta: ProZoekMeta | undefined;
  hoofdmappen: ProHoofdmapInfo[];
  onChange: (next: ProFilters) => void;
}> = ({ filters, vakgebied, meta, hoofdmappen, onChange }) => {
  const { t } = useTranslation('pages');
  const zet = (patch: Partial<ProFilters>) => onChange({ ...filters, ...patch });
  const tags = filterTags(filters, vakgebied, hoofdmappen, meta?.registers ?? REGISTERS_FALLBACK, t, zet);
  if (tags.length === 0 && !heeftFilters(filters)) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label={t('proAcademy.actieveFilters', { defaultValue: 'Actieve filters' })}>
      {tags.map((tag) => (
        <span key={tag.key} className="inline-flex items-center gap-1 rounded-full border border-sky-400/40 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-800">
          {tag.label}
          <button type="button" onClick={tag.wis} aria-label={t('proAcademy.wisFilter', { defaultValue: 'Filter {{f}} verwijderen', f: tag.label })} className="rounded-full p-0.5 hover:bg-sky-100">
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        </span>
      ))}
      <button type="button" onClick={() => onChange({ ...LEGE_FILTERS, hobby: filters.hobby, sort: filters.sort })} className="ml-1 text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline">
        {t('proAcademy.allesWissen', { defaultValue: 'Alles wissen → terug naar het overzicht' })}
      </button>
    </div>
  );
};

export default ProAcademyFilterbalk;
